"use client";

/**
 * The ONE credit ring (owner feature, 2026-08-27) — extracted the same day it
 * shipped, because it had immediately been pasted twice (composer +
 * account-dropdown line) with different radii, and the low-credits threshold
 * and brand colors were literals in both files: the exact three-diverging-
 * copies drift the "Shared components before copies" rule exists to prevent.
 *
 * Hook-free and label-driven, like `PackageCardContent`: the caller supplies
 * the visible text; the ring only draws the fraction.
 */

/** At or below this many remaining, the ring and text turn red. */
export const LOW_CREDITS = 5;

export function QuotaRing({ used, cap, label, size = 16, lowAt = LOW_CREDITS }: {
  used: number;
  cap: number;
  /** The visible text beside the ring — it is its own accessible name. */
  label: string;
  size?: number;
  /**
   * At or below this many remaining, the ring turns red. Defaults to the chat's
   * threshold. It HAS to be a prop: the free-tier inquiry counter has a cap of
   * 3, so the chat's `LOW_CREDITS = 5` would paint it red from the very first
   * render — a warning colour that never means anything.
   */
  lowAt?: number;
}) {
  const remaining = Math.max(0, cap - used);
  const frac = cap > 0 ? remaining / cap : 0;
  const r = size * 0.40625; // 6.5 at the 16px reference size
  const C = 2 * Math.PI * r;
  const low = remaining <= lowAt;
  return (
    <span className={`inline-flex shrink-0 items-center gap-5 whitespace-nowrap text-[0.75rem] tabular-nums ${low ? "text-red" : "text-gray-600"}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: "var(--navy)" }} strokeOpacity="0.12" strokeWidth={size * 0.15625} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          style={{ stroke: low ? "var(--red)" : "var(--gold)" }} strokeWidth={size * 0.15625} strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - frac)}
          className="transition-[stroke-dashoffset] duration-300 ease-out"
        />
      </svg>
      {label}
    </span>
  );
}
